import getColumnConstraintWithRowData from 'notebook/structured-tables/columns/getColumnConstraint';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {RowData} from 'notebook/tables/constants/RowConstants';
import {ColumnConstraint} from 'notebook/tables/constants/TableColumnConstants';
import {getErrorOrWarningFromCell} from 'notebook/tables/errors/CellErrorGetters';
import {TriangleState} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell, EvaluatedCellError} from 'notebook/tables/excess/Evaluator';
import {computeErrorState} from 'notebook/tables/store/logic/TableCellLogic';
import {RowModel} from 'notebook/tables/store/models/RowModel';
import {
  createDebuggableSelector,
  createDeepSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore} from 'notebook/tables/store/TableStore';
import {formatTypedValue} from 'notebook/tables/typed-values/DataTypes';
import {isTextEditableType} from 'notebook/tables/typed-values/TypedValueConstants';

/**
 * This contains volatile values, or values that frequently changes due to evaluation or renders,
 * from the RowModel or TableModel that are only relevant to this single cell.
 *
 * Everything inside here will be deep-compare memoized.
 */
interface VolatileCellModel {
  cell: Cell;
  evaluatedCell: EvaluatedCell;
}

/**
 * This model is what is passed down to the components.
 * This can contain data that is derived from the RowModel, TableModel, or TableEditableStore.
 */
export interface CellModel extends VolatileCellModel {
  rowData: RowData | null;
  columnConstraint: ColumnConstraint | null;
  error: EvaluatedCellError | undefined;
  errorState: TriangleState;
}

const createVolatileCellModelSelector = (
  rowSelector: (store: TableEditableStore) => RowModel,
  columnIndex: number
): ((store: TableEditableStore) => VolatileCellModel) =>
  createDeepSelector.withTypes<TableEditableStore>()(
    [(store) => rowSelector(store).row[columnIndex], (store) => rowSelector(store).evaluatedRow[columnIndex]],
    (cell, evaluatedCell) => ({cell, evaluatedCell})
  );

export const createCellModelSelector = (
  rowSelector: (store: TableEditableStore) => RowModel,
  columnIndex: number
): ((store: TableEditableStore) => CellModel) =>
  createDebuggableSelector.withTypes<TableEditableStore>()(
    [
      // volatileCellModel
      createVolatileCellModelSelector(rowSelector, columnIndex),
      // rowData
      (store) => rowSelector(store).rowData,
      // columnConstraint
      (store) => rowSelector(store).columnConstraints[columnIndex],
      // isTombstoned
      (store) => rowSelector(store).rowProperties.isTombstoned,
    ],
    (volatileCellModel, rowData, columnConstraint, isTombstoned): CellModel => {
      const cell: Cell = {
        ...volatileCellModel.cell,
        text:
          volatileCellModel.cell.text ||
          (volatileCellModel.cell.typedValue != null && isTextEditableType(volatileCellModel.cell.typedValue)
            ? formatTypedValue(volatileCellModel.cell.typedValue)
            : ''),
        // grayOutContents is only set for StructuredTables
        ...(isTombstoned && {grayOutContents: true}),
      };
      const mergedError = getErrorOrWarningFromCell(cell, volatileCellModel.evaluatedCell);
      return {
        cell,
        evaluatedCell: volatileCellModel.evaluatedCell,
        rowData,
        columnConstraint: getColumnConstraintWithRowData(columnConstraint, rowData),
        error: mergedError,
        errorState: computeErrorState(mergedError),
      };
    }
  );
